import React, { useState, useRef, useEffect } from "react";
import { getImageUrl, trackEvent, useGaTrackingConfig } from "../Utility/Utility";

const MobileSlider = ({
  groupedCarouselData,
  autoSlideInterval = 4000,
  defaultBanner,
  fireViewPromotion,
  promotionPrefix = "top_banner",
  imageKey = "mobileImage",
  autoplay,
  infinite,
  showDots,
  slidesToShow,
  slidesToScroll,
  placement
}) => {
  const mobileSlideGap = 8;
  const gaTrackingConfig = useGaTrackingConfig();
  const normalizedSlidesToShow = Number.isFinite(parseFloat(slidesToShow))
    ? Math.max(1, parseFloat(slidesToShow))
    : 1;
  const hasFractionalSlidesToShow = normalizedSlidesToShow % 1 !== 0;
  const effectiveSlideGap = hasFractionalSlidesToShow ? 0 : mobileSlideGap;
  const renderedSlidesPerGroup = Math.max(1, Math.ceil(normalizedSlidesToShow));
  const normalizedSlidesToScroll = Number.isFinite(parseInt(slidesToScroll, 10))
    ? Math.max(1, parseInt(slidesToScroll, 10))
    : 1;

  const [currentIndex, setCurrentIndex] = useState(
    infinite ? groupedCarouselData.length || 0 : 0
  );
  const [isTransitioning, setIsTransitioning] = useState(true);
  const [isResetting, setIsResetting] = useState(false);
  const sliderRef = useRef(null);
  const autoSlideRef = useRef(null);
  const resetTimeoutRef = useRef(null);
  const isInViewRef = useRef(false);
  const currentIndexMobileRef = useRef(currentIndex);
  const groupedDataMobileRef = useRef(groupedCarouselData);

  // Extend slides for infinite effect
  const extendedData = [
    ...groupedCarouselData,
    ...groupedCarouselData,
    ...groupedCarouselData
  ];

  const renderData = infinite ? extendedData : groupedCarouselData;

  const getRealIndex = (idx, total) => {
    if (!total) return 0;
    if (!infinite) {
      return Math.max(0, Math.min(idx, total - 1));
    }
    return ((idx % total) + total) % total;
  };

  useEffect(() => {
    setCurrentIndex(infinite ? groupedCarouselData.length : 0);
  }, [groupedCarouselData, infinite]);

  useEffect(() => { currentIndexMobileRef.current = currentIndex; }, [currentIndex]);
  useEffect(() => { groupedDataMobileRef.current = groupedCarouselData; }, [groupedCarouselData]);

  useEffect(() => {
    if (!infinite) return undefined;

    const n = groupedCarouselData.length;
    if (!n) return;

    // Clear any existing timeout first
    if (resetTimeoutRef.current) {
      clearTimeout(resetTimeoutRef.current);
    }

    // Forward: reached end copy
    if (currentIndex >= 2 * n) {
      setIsResetting(true);
      resetTimeoutRef.current = setTimeout(() => {
        setIsTransitioning(false);
        setCurrentIndex(currentIndex - n); // jump back to real slides
        requestAnimationFrame(() => {
          setIsTransitioning(true);
          setIsResetting(false);
        });
      }, 500);
    }

    // Backward: reached start copy
    if (currentIndex < n) {
      setIsResetting(true);
      resetTimeoutRef.current = setTimeout(() => {
        setIsTransitioning(false);
        setCurrentIndex(currentIndex + n); // jump forward to real slides
        requestAnimationFrame(() => {
          setIsTransitioning(true);
          setIsResetting(false);
        });
      }, 500);
    }

    // Cleanup on unmount
    return () => clearTimeout(resetTimeoutRef.current);

  }, [currentIndex, groupedCarouselData.length, infinite]);

  // Swipe handling
  useEffect(() => {
    const slider = sliderRef.current;
    if (!slider) return;

    let startX = 0;
    let isDown = false;

    const handleTouchStart = (e) => {
      isDown = true;
      startX = e.touches[0].clientX;
    };

    const handleTouchMove = (e) => {
      if (!isDown || isResetting) return;
      const moveX = e.touches[0].clientX;
      const diff = startX - moveX;

      if (Math.abs(diff) > 50) {
        setIsTransitioning(true);
        if (diff > 0) {
          setCurrentIndex((prev) => {
            if (!infinite) {
              return Math.min(prev + normalizedSlidesToScroll, groupedCarouselData.length - 1);
            }
            return prev + normalizedSlidesToScroll;
          });
        } else {
          setCurrentIndex((prev) => {
            if (!infinite) {
              return Math.max(prev - normalizedSlidesToScroll, 0);
            }
            return prev - normalizedSlidesToScroll;
          });
        }
        isDown = false;
        // **Reset auto-slide timer**
        handleUserInteraction();
      }
    };

    const handleTouchEnd = () => (isDown = false);

    slider.addEventListener("touchstart", handleTouchStart);
    slider.addEventListener("touchmove", handleTouchMove);
    slider.addEventListener("touchend", handleTouchEnd);

    return () => {
      slider.removeEventListener("touchstart", handleTouchStart);
      slider.removeEventListener("touchmove", handleTouchMove);
      slider.removeEventListener("touchend", handleTouchEnd);
    };
  }, [groupedCarouselData.length, infinite, isResetting, normalizedSlidesToScroll]);

  // Auto-slide
  const handleUserInteraction = () => {
    if (autoSlideRef.current) clearInterval(autoSlideRef.current);
    startAutoSlide(); // restart the timer
  };

  const startAutoSlide = () => {
    if (autoSlideRef.current) clearInterval(autoSlideRef.current);

    if (!autoplay) return;

    autoSlideRef.current = setInterval(() => {
      if (isResetting) return;
      setCurrentIndex((prev) => {
        if (infinite) return prev + normalizedSlidesToScroll;
        if (!groupedCarouselData.length) return prev;
        return prev < groupedCarouselData.length - 1
          ? Math.min(prev + normalizedSlidesToScroll, groupedCarouselData.length - 1)
          : prev;
      });
      setIsTransitioning(true);
    }, autoSlideInterval);
  };

  useEffect(() => {
    startAutoSlide();
    return () => clearInterval(autoSlideRef.current);
  }, [autoplay, autoSlideInterval, groupedCarouselData.length, infinite, isResetting, normalizedSlidesToScroll]);

  // IO: detect when slider enters/leaves viewport and fire for current banner
  useEffect(() => {
    const slider = sliderRef.current;
    if (!slider || !fireViewPromotion) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        isInViewRef.current = entry.isIntersecting;
        if (entry.isIntersecting) {
          const data = groupedDataMobileRef.current;
          const n = data.length;
          if (!n) return;
          const idx = currentIndexMobileRef.current;
          const realIndex = getRealIndex(idx, n);
          const group = data[realIndex];
          if (group) fireViewPromotion(group, realIndex);
        }
      },
      { threshold: 0.5 }
    );
    observer.observe(slider);
    return () => observer.disconnect();
  }, [fireViewPromotion]);

  // Fire view_promotion when the visible slide changes (skip during infinite-scroll reset)
  useEffect(() => {
    if (!fireViewPromotion || isResetting || !isInViewRef.current) return;
    const n = groupedCarouselData.length;
    if (!n) return;
    const realIndex = getRealIndex(currentIndex, n);
    const group = groupedCarouselData[realIndex];
    if (group) fireViewPromotion(group, realIndex);
  }, [currentIndex, fireViewPromotion, groupedCarouselData, infinite, isResetting]);

  const handleDotClick = (index) => {
    if (infinite) {
      setCurrentIndex(index + groupedCarouselData.length);
      return;
    }
    setCurrentIndex(index);
  };
  return (
    <div
      className={placement === "Main" ? "mobile-slider-Top" : placement === "Bottom" ? "mobile-slider-container-bottom" : "mobile-slider-container"}
      ref={sliderRef}
      style={{
        "--mobile-slides-to-show": normalizedSlidesToShow,
        "--mobile-slide-gap": `${effectiveSlideGap}px`,
      }}
    >

      <section className="section-hero">
        <div className="mobileTitle">
          <div className="container">
            {renderData?.[0]?.[0]?.title && <h2 className="groupedTitle">{renderData?.[0]?.[0]?.title}</h2>}
            {renderData?.[0]?.[0]?.subtitle && <h3 className="groupSubtitle">{renderData?.[0]?.[0]?.subtitle}</h3>}
          </div>


        </div>
        <div className="container">
          <div
            className="hero justify-content-start top-slider"
            style={{
              whiteSpace: "nowrap",
              overflow: "hidden",
              position: "relative",
              width: "100%",
            }}
          >
            {groupedCarouselData?.length === 1 &&
              groupedCarouselData[0].map(item => (
                <div className="onlyOneImg">
                  <img
                    className="banner_image_width"
                    src={getImageUrl(item?.mobileImage)}
                    alt={item?.mobileImage?.substring(
                      item?.mobileImage?.lastIndexOf("/") + 1,
                      item?.mobileImage?.lastIndexOf(".")
                    )}

                    onClick={() => {
                      sessionStorage.setItem("ga4_item_list_name", `${promotionPrefix}_1`);
                      trackEvent("select_promotion", {
                        page_type: "home_page",
                        // ...gaTrackingConfig.trackingContext,
                        client_code: gaTrackingConfig.trackingContext.client_code,
                        platform_code: gaTrackingConfig.trackingContext.platform_code,
                        ecommerce: {
                          promotion_id: item?.mobileImage?.substring(
                            item?.mobileImage?.lastIndexOf("/") + 1,
                            item?.mobileImage?.lastIndexOf(".")
                          ),
                          promotion_name: `${promotionPrefix}_1`,
                          creative_name: getImageUrl(item?.mobileImage),
                          creative_slot: "1",
                        },
                      });
                      window.location.href = item?.link;
                    }}
                    deb
                    key={item?.id || item?.[imageKey]}
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = defaultBanner;
                    }}
                  />
                </div>
              ))}
            {groupedCarouselData?.length > 1 && (
              <div
                className="mobile-slider-track"
                style={{
                  // display: "flex",
                  display: "block",
                  float: "left",
                  transform: `translateX(-${currentIndex * 100}%)`,
                  transition: isTransitioning ? "transform 0.5s ease-in-out" : "none",
                }}
              >
                {renderData?.map((group, index) => (


                  <div
                    key={index}
                    className="slide"
                  >

                    {(group || []).slice(0, renderedSlidesPerGroup).map((item, itemIndex) => {
                      const itemImage = item?.mobileImage || item?.[imageKey];
                      const itemSlot = item?._position || (itemIndex + 1);
                      return (
                        <div className="slider_img_box">
                          <img
                            className="banner_image_width"
                            src={`${process.env.REACT_APP_CLIENT_URL}${itemImage}`}
                            alt={itemImage?.substring(
                              itemImage?.lastIndexOf("/") + 1,
                              itemImage?.lastIndexOf(".")
                            )}
                            key={`${index}-${itemIndex}-${itemSlot}`}
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = defaultBanner;
                            }}
                            onClick={() => {
                              sessionStorage.setItem("ga4_item_list_name", `${promotionPrefix}_${itemSlot}`);
                              trackEvent("select_promotion", {
                                page_type: "home_page",
                                ...gaTrackingConfig.trackingContext,
                                ecommerce: {
                                  promotion_id: itemImage?.substring(
                                    itemImage?.lastIndexOf("/") + 1,
                                    itemImage?.lastIndexOf(".")
                                  ),
                                  promotion_name: `${promotionPrefix}_${itemSlot}`,
                                  creative_name: getImageUrl(itemImage),
                                  creative_slot: itemSlot.toString(),
                                },
                              });
                              window.location.href = item?.link;
                            }}
                          />
                        </div>
                      );
                    })}

                  </div>

                ))}

              </div>
            )}
          </div>
          {showDots && groupedCarouselData?.length > 1 && (
            <div className="dots-container text-center">
              {groupedCarouselData?.map((_, index) => (
                <span
                  key={index}
                  className={`dot1 ${index === getRealIndex(currentIndex, groupedCarouselData.length) ? "active" : ""
                    }`}
                  onClick={() => handleDotClick(index)}
                ></span>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default MobileSlider;
